import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL, authenticatedFetch, getSchedulerErrorCount } from '../utils/api';
import { startActivityAwarePolling } from '../utils/activityAwarePolling';

const NotificationsContext = createContext({
  embeddingCount: 0,
  embeddingNeededCount: 0,
  erroredEmbeddingCount: 0,
  reviewCount: 0,
  totalCount: 0,
  schedulerErrorCount: 0,
  embeddingSources: [],
  erroredEmbeddingSources: [],
  reviewSources: [],
  refresh: () => {},
});

export function useNotifications() {
  return useContext(NotificationsContext);
}

const POLL_INTERVAL_MS = 60_000;

export function NotificationsProvider({ children }) {
  const [notifications, setNotifications] = useState({
    embeddingCount: 0,
    embeddingNeededCount: 0,
    erroredEmbeddingCount: 0,
    reviewCount: 0,
    totalCount: 0,
    schedulerErrorCount: 0,
    embeddingSources: [],
    erroredEmbeddingSources: [],
    reviewSources: [],
  });
  const fetchingRef = useRef(false);

  const fetchNotifications = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const url = new URL(
        `${API_BASE_URL}/casc/v1/api/horizon-management/notifications`,
        window.location.origin
      );
      const response = await authenticatedFetch(url.toString(), { method: 'GET' });
      let data = null;
      if (response.ok) {
        data = await response.json().catch(() => null);
      }

      let schedulerErrorCount = 0;
      try {
        const sched = await getSchedulerErrorCount();
        schedulerErrorCount = Number(sched?.count) || 0;
      } catch (_) {
        /* ignore — migration may not be applied yet */
      }

      setNotifications((prev) => {
        const nextSources = data?.embeddingSources || [];
        const prevSources = prev.embeddingSources || [];
        const sourcesUnchanged =
          prevSources.length === nextSources.length &&
          prevSources.every(
            (s, i) =>
              s.id === nextSources[i]?.id &&
              s.reason === nextSources[i]?.reason &&
              s.lastIngestionAt === nextSources[i]?.lastIngestionAt &&
              s.lastEmbeddedAt === nextSources[i]?.lastEmbeddedAt
          );
        const next = {
          embeddingCount: data?.embeddingCount || 0,
          embeddingNeededCount: data?.embeddingNeededCount || 0,
          erroredEmbeddingCount: data?.erroredEmbeddingCount || 0,
          reviewCount: data?.reviewCount || 0,
          totalCount: data?.totalCount || 0,
          schedulerErrorCount,
          embeddingSources: nextSources,
          erroredEmbeddingSources: data?.erroredEmbeddingSources || [],
          reviewSources: data?.reviewSources || [],
        };
        if (
          prev.embeddingCount === next.embeddingCount &&
          prev.embeddingNeededCount === next.embeddingNeededCount &&
          prev.erroredEmbeddingCount === next.erroredEmbeddingCount &&
          prev.reviewCount === next.reviewCount &&
          prev.totalCount === next.totalCount &&
          prev.schedulerErrorCount === next.schedulerErrorCount &&
          sourcesUnchanged
        ) {
          return prev;
        }
        return next;
      });
    } catch (_) {
      // Network error — silently ignore, keep previous values
    } finally {
      fetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    return startActivityAwarePolling(fetchNotifications, POLL_INTERVAL_MS, { immediate: true });
  }, [fetchNotifications]);

  useEffect(() => {
    const handler = () => fetchNotifications();
    window.addEventListener('dataReviewUpdate', handler);
    window.addEventListener('refreshStats', handler);
    window.addEventListener('schedulerUpdate', handler);
    return () => {
      window.removeEventListener('dataReviewUpdate', handler);
      window.removeEventListener('refreshStats', handler);
      window.removeEventListener('schedulerUpdate', handler);
    };
  }, [fetchNotifications]);

  return (
    <NotificationsContext.Provider value={{ ...notifications, refresh: fetchNotifications }}>
      {children}
    </NotificationsContext.Provider>
  );
}
